// End the user's session by bouncing to the customer portal's /logout
// route. The portal owns the POST /auth/logout call — it's the single
// source of truth for session lifecycle (cookie set on login, cleared on
// logout). avatar-web only needs to:
//   - close the Convorcher WebSocket so the next session doesn't reuse the
//     stale session_id;
//   - clear the localStorage keys this app writes for the **current user**
//     (ENG-559: keys are namespaced by user id, so we must clear only this
//     user's keys and leave any other user's keys on a shared browser intact);
//   - then redirect to portal /logout?return_to=<our origin>/, which will
//     POST /auth/logout and bounce back.

import { redirectToLogout } from '../portal'
import { getCurrentUserId } from '../userScope'

// Keys that never carry a charId suffix.
const STATIC_BASES = ['convorcher_session_id', 'aos-character-v1']
// Keys that may carry a trailing `-{charId}` segment.
// Current versions FIRST (clearing the live cache is the point), with recent
// prior versions kept as a cleanup safety net for lingering namespaced caches.
// Unlike legacyKeyMigration's frozen list, this one MUST track the current
// storage-key versions — a missing current version leaves that draft behind on
// logout. Explicit (not a glob) on purpose: a blanket `aos-blueprint` prefix
// would wipe OTHER users' keys on a shared browser.
const CHAR_BASES = [
  'aos-character-authoring-v4',
  'aos-blueprint-v6',
  'aos-blueprint-face-v7',
  'aos-blueprint-face-v6',
  'aos-blueprint-events-v7',
  'aos-blueprint-events-v6',
  'aos-blueprint-events-v5',
]

// Decide whether a localStorage key belongs to the logging-out user. Static
// keys also match their legacy un-namespaced form (safety net for any key that
// predates migration). Per-character keys match only the current user's
// namespace — never a blanket prefix that would wipe another user's keys.
function shouldClear(key, uid) {
  for (const base of STATIC_BASES) {
    if (key === base) return true
    if (uid && key === `${base}-${uid}`) return true
  }
  if (uid) {
    for (const base of CHAR_BASES) {
      if (key === `${base}-${uid}` || key.startsWith(`${base}-${uid}-`)) return true
    }
  }
  return false
}

function clearLocalStorage() {
  if (typeof window === 'undefined') return
  const uid = getCurrentUserId()
  try {
    // Snapshot first — removing mid-iteration is implementation-defined.
    for (const key of Object.keys(window.localStorage)) {
      if (shouldClear(key, uid)) {
        try { window.localStorage.removeItem(key) } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
}

function disconnectConvorcher() {
  if (typeof window === 'undefined') return
  try { window.__CONVORCHER_CLIENT__?.stopAutoConnect() } catch { /* ignore */ }
}

export function logout() {
  disconnectConvorcher()
  clearLocalStorage()
  redirectToLogout()
}
